import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { ReactComponent as RightCaret } from '../Icons/RightCaret.svg';
import { CropAmounts, formatCurrency, PricingCropKey } from './pricingMatrix';

/** One discount row on the Pricing page: fixed Corn | Soybean | Discount | Net after columns, optionally expandable. */
export function DiscountSection(props: {
	className: string;
	title: string;
	tag?: string;
	meta?: React.ReactNode;
	/** Inline control in the meta area (the Local Grower percent input). */
	control?: React.ReactNode;
	crops: PricingCropKey[];
	cropAmounts?: CropAmounts;
	cropPercents?: CropAmounts;
	discountTotal?: number;
	netAfter?: number;
	expandable: boolean;
	/** Greyed - present but not applied (e.g. Order Deadline passed). */
	dimmed?: boolean;
	defaultOpen?: boolean;
	children?: React.ReactNode;
})
{
	const { className, title, tag, meta, control, crops, cropAmounts, cropPercents, discountTotal, netAfter, expandable, dimmed, defaultOpen, children } = props;
	const theme = useTheme();
	const [open, setOpen] = useState(!!defaultOpen && expandable);
	const textColor = dimmed ? theme.colors.lightGrey : theme.colors.darkGrey;
	const labelStyle: React.CSSProperties = { fontSize: theme.fontSizes.tiny, fontWeight: theme.fontWeights.normal, color: theme.colors.mediumGrey };
	const numberColumn = (label: string, width: string, value?: React.ReactNode) =>
		<div style={{ width, textAlign: 'right', fontWeight: theme.fontWeights.bold, flexShrink: 0 }}>
			<div style={labelStyle}>{label}</div>
			{value || '—'}
		</div>;

	return (
		<div className={className} style={{ border: `1px solid ${theme.colors.lightestGrey}`, borderRadius: 4, marginTop: 12 }}>
			<div
				className={`${className}_Header`}
				onClick={() => expandable && setOpen(prev => !prev)}
				style={{
					display: 'flex',
					alignItems: 'center',
					padding: '9px 15px 9px 10px',
					cursor: expandable ? 'pointer' : 'default',
					backgroundColor: theme.colors.backgroundLM,
					color: textColor,
				}}
			>
				<div style={{ width: 26, flexShrink: 0, display: 'flex', alignItems: 'center', visibility: expandable ? 'visible' : 'hidden' }}>
					<RightCaret style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
				</div>
				<div style={{ width: '22%', flexShrink: 0, fontWeight: theme.fontWeights.bold, fontSize: theme.fontSizes.normal, display: 'flex', alignItems: 'center', gap: 8 }}>
					{title}
					{tag &&
						<span style={{
							fontSize: theme.fontSizes.tiny,
							fontWeight: theme.fontWeights.normal,
							borderRadius: 3,
							padding: '1px 6px',
							backgroundColor: theme.colors.lightestGrey,
							color: theme.colors.mediumGrey,
						}}>
							{tag}
						</span>}
				</div>
				<div
					onClick={evt => control && evt.stopPropagation()}
					style={{ flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: theme.fontSizes.small, color: dimmed ? theme.colors.lightGrey : theme.colors.mediumGrey }}
				>
					{control}
					{meta}
				</div>
				{crops.map(key =>
				{
					const amount = cropAmounts?.[key];
					return numberColumn(key, '11%', typeof amount === 'number' && <>
						{formatCurrency(amount)}
						{typeof cropPercents?.[key] === 'number' &&
							<span style={{ fontWeight: theme.fontWeights.normal, color: theme.colors.mediumGrey }}>{` (${cropPercents[key]}%)`}</span>}
					</>);
				})}
				{numberColumn('Discount', '13%', typeof discountTotal === 'number' && `− ${formatCurrency(discountTotal)}`)}
				{numberColumn('Net after', '12%', typeof netAfter === 'number' && formatCurrency(netAfter))}
			</div>
			{open &&
				<div className={`${className}_Body`} style={{ padding: '12px 15px 16px', borderTop: `1px solid ${theme.colors.lightestGrey}` }}>
					{children}
				</div>}
		</div>
	);
}
